/* eslint-disable react-hooks/exhaustive-deps */
import { TrackingEventTypes } from '@eagle/data-function-types';
import { useTheme } from '@mui/material';
import L from 'leaflet';
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { Marker } from 'react-leaflet';
import { MapPanes, Maybe } from '../../../../types';
import { useHistorySearch, useNotableEventsContext } from '../../../entity-journey';
import { EventLocationData } from '../../thing-event-pane/thing-event-pane.types';
import { getNotableEventColor } from '../route-matching/clustering.utils';
import { createClusterMarker, POINT_RADIUS } from './cluster.utils';

interface ClusterPointProps {
  calculatedHeading: number;
  center: L.LatLng;
  event: EventLocationData;
  firstPoint?: boolean;
  hoveredEventId: Maybe<string>;
  iconColor: string;
  isOngoing?: boolean;
  lastPoint?: boolean;
  markerSize?: number;
  onClick: () => void;
  opacity: boolean;
  setHoveredEventId?: Dispatch<SetStateAction<Maybe<string>>>;
  setSelectedEvent?: Dispatch<SetStateAction<Maybe<EventLocationData>>>;
}

export const EventClusterPoint: FC<ClusterPointProps> = ({
  calculatedHeading,
  center,
  event,
  firstPoint = false,
  hoveredEventId,
  iconColor,
  isOngoing,
  lastPoint = false,
  markerSize = POINT_RADIUS,
  onClick,
  opacity,
  setHoveredEventId,
  setSelectedEvent,
}) => {
  const markerRef = useRef<L.Marker>(null);
  const theme = useTheme();
  const { notableEvents } = useNotableEventsContext();
  const color = useMemo(() => getNotableEventColor(event, theme, notableEvents), [event]);
  const { setClickedEventId } = useHistorySearch();

  useEffect(() => {
    if (event.eventId === hoveredEventId) {
      markerRef.current?.openTooltip();
      return;
    }
    markerRef.current?.closeTooltip();
  }, [event, hoveredEventId, markerRef.current]);

  return <>
    <Marker
      ref={markerRef}
      eventHandlers={{
        click: (e) => {
          e.originalEvent.stopPropagation();
          setSelectedEvent?.(event);
          setClickedEventId(event.eventId);
          onClick();
        },
        mousemove: () => setHoveredEventId?.(event.eventId),
        mouseout: () => setHoveredEventId?.(null),
      }}
      icon={
        createClusterMarker(
          color,
          markerSize,
          firstPoint,
          lastPoint,
          Boolean(isOngoing && lastPoint),
          iconColor,
          event.eventTypeId === TrackingEventTypes.LOCATION_UPDATE,
          theme,
          event.data.velocity?.heading ?? calculatedHeading,
        )
      }
      opacity={opacity ? 1 : 0}
      pane={MapPanes.BREADCRUMBS}
      position={center}
      title={event.eventId}
    />
  </>;
};
